import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Loader } from 'lucide-react';
import useUser from '@/hooks/useUser';
import { toast } from 'react-hot-toast';
import { EmailInput } from './EmailInput';
import { GoogleButton } from '@/components/Auth';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { isDev } from '@/api/config';
import {
  checkDomainPublicPublicCompaniesDomainCheckPublicIdGet,
  getMemberInvitationOptions,
} from '@/api';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import InvitationInfo from './InvitationInfo';
import { PRESSABLE_CLASS } from './AuthPageShell';
import { cn } from '@/lib/utils';

// Generate random email for development
const generateRandomDevEmail = (): string => {
  const randomString = Math.random().toString(36).substring(2, 10);
  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
  const randomDomain = domains[Math.floor(Math.random() * domains.length)];
  const baseEmail = `user${randomString}@${randomDomain}`;
  return baseEmail.replace('@', '+dev@');
};

function RegistrationForm() {
  const { googleUser } = useAuth();
  const navigate = useNavigate();
  const [accountExists, setAccountExists] = useState(false);
  const {
    createCompanyAccountMutation,
    createCompanyAccountWithGoogleMutation,
    loginGoogle,
    acceptMemberInvitation,
  } = useUser();

  const { token, returnTo } = useSearch({ from: '/register' });
  const { data: invitation } = useQuery({
    ...getMemberInvitationOptions({
      query: {
        token: token || '',
      },
    }),
    enabled: !!token
  }
  );

  // Check if this is an invite flow
  const isInviteFlow = !!token;
  const hideCompanyField = isInviteFlow;
  // Create schema dynamically based on Google user presence and invite flow
  const registrationSchema = z.object({
    // Company name is only required for non-invite and non-single-user flows
    name: hideCompanyField
      ? z.string().optional()
      : z.string().min(1, 'Workspace name is required'),
    email: z.string().email('Please enter a valid email address'),
    // Password is only required if there's no Google user
    password: googleUser
      ? z.string().optional()
      : z.string().min(8, 'Password must be at least 8 characters'),
    full_name: z.string().min(1, 'Full name is required'),
    account_type: isInviteFlow ? z.literal('user') : z.literal('company'),
  });

  type RegistrationFormValues = z.infer<typeof registrationSchema>;

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      name: hideCompanyField
        ? undefined
        : googleUser
          ? `${`${googleUser.first_name || ''} ${googleUser.last_name || ''}`.trim() || 'My'}'s workspace`
          : '',
      email: !token
        ? googleUser?.email || (isDev ? generateRandomDevEmail() : '')
        : invitation?.email,
      password: googleUser ? undefined : '',
      full_name: googleUser
        ? `${googleUser.first_name || ''} ${googleUser.last_name || ''}`.trim()
        : '',
      account_type: isInviteFlow ? 'user' : 'company',
    },
  });

  useEffect(() => {
    if (invitation) {
      form.setValue('email', invitation.email);
    }
  }, [invitation]);
  // Company name validation mutation
  const validateCompanyNameMutation = useMutation({
    mutationFn: async (name: string) => {
      return await checkDomainPublicPublicCompaniesDomainCheckPublicIdGet({
        path: {
          public_id: name,
        },
      });
    },
    onError: (error: any) => {
      console.error('Company name validation failed:', error);
      toast.error('Cannot validate company name. Please try a different one.');
    },
  });

  const onSubmit = async (values: RegistrationFormValues) => {
    if (isLoading) return;
    if (accountExists) {
      navigate({
        to: '/login',
        search: returnTo ? { returnTo } : undefined,
      });
      return;
    }
    try {
      // Split full name into first and last name
      const nameParts = values.full_name.trim().split(/\s+/);
      const first_name = googleUser?.first_name || nameParts[0] || '';
      const last_name =
        googleUser?.last_name ||
        (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

      const userData = {
        ...values,
        email: googleUser?.email || values.email,
        first_name,
        last_name,
        uid: googleUser?.uid || '',
        profile_image: googleUser?.profile_image || '',
        provider: googleUser ? 'google' : 'email',
        password: values.password || null,
        name: values.name!,
      };
      // For invite flows, just create user without company
      if (isInviteFlow) {
        acceptMemberInvitation.mutate({
          token: token || '',
          user_details: userData,
        });
        return;
      }

      // For normal flows, validate company name first and create company
      const isValid = await validateCompanyNameMutation.mutateAsync(
        values.name!,
      );

      if (!isValid) {
        toast.error(
          'Company name is already taken. Please choose a different name.',
        );
        return;
      }

      // Create account based on auth method
      if (googleUser) {
        createCompanyAccountWithGoogleMutation.mutate({
          body: {
            ...userData,
            account_type: 'company_admin',
          },
        });
      } else {
        // Password should always exist when not using Google auth
        if (!values.password) {
          toast.error('Password is required');
          return;
        }

        createCompanyAccountMutation.mutate({
          email: values.email,
          password: values.password,
          data: {
            ...userData,
            account_type: 'company_admin',
          },
        });
      }
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error('Registration submission failed:', error);
    }
  };

  // Success stays busy: the next page's code can take seconds to load on slow
  // networks, and a live Continue button invited second submits (EMAIL_EXISTS,
  // duplicate Google accounts).
  const isLoading =
    validateCompanyNameMutation.isPending ||
    createCompanyAccountMutation.isPending ||
    createCompanyAccountMutation.isSuccess ||
    createCompanyAccountWithGoogleMutation.isPending ||
    createCompanyAccountWithGoogleMutation.isSuccess ||
    acceptMemberInvitation.isPending ||
    acceptMemberInvitation.isSuccess;

  return (
    <div className="w-full max-w-sm">
      <div className="mb-6 space-y-1">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {isInviteFlow ? 'Join your team' : 'Create account'}
        </h2>
        {!isInviteFlow && (
          <p className="text-sm text-muted-foreground">
            Voice sessions to learn anything, or to screen people.
          </p>
        )}
      </div>

      {isInviteFlow && <InvitationInfo />}

      {!googleUser && (
        <>
          <GoogleButton
            onClick={() => {
              loginGoogle();
            }}
            className="w-full mb-4">
            Continue with Google
          </GoogleButton>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white dark:bg-gray-950 px-3 text-muted-foreground">or</span>
            </div>
          </div>
        </>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoFocus
                    placeholder="Full name"
                    className="h-10 rounded-sm"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      if (hideCompanyField) return;
                      const trimmed = e.target.value.trim();
                      const currentName = form.getValues('name') || '';
                      const looksAutoFilled =
                        !currentName || currentName.endsWith("'s workspace");
                      if (looksAutoFilled) {
                        form.setValue(
                          'name',
                          trimmed ? `${trimmed}'s workspace` : '',
                          { shouldValidate: true },
                        );
                      }
                    }}
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!hideCompanyField && (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Workspace name"
                      className="h-10 rounded-sm"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {!googleUser && (
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <EmailInput
                  value={field.value || invitation?.email || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  disabled={isLoading || isInviteFlow}
                  required
                  returnTo={returnTo}
                  onAccountExists={(exists) => setAccountExists(exists)}
                />
              )}
            />
          )}

          {!googleUser && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Password (min 8 characters)"
                      className="h-10 rounded-sm"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Button
            type="submit"
            disabled={isLoading || accountExists}
            className={cn(
              'w-full h-10 rounded-sm bg-gray-900 hover:bg-gray-800 text-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 font-medium',
              PRESSABLE_CLASS,
            )}>
            {isLoading && <Loader className="h-4 w-4 animate-spin mr-2" />}
            {isLoading
              ? 'Setting up your workspace...'
              : isInviteFlow
                ? 'Accept invitation'
                : 'Continue'}
          </Button>
        </form>
      </Form>

      <div className="flex items-center justify-center text-xs text-muted-foreground pt-3">
        <span>
          Have an account?{' '}
          <Link
            to="/login"
            search={{ returnTo, token }}
            className="hover:text-primary transition-colors"
          >
            Sign in
          </Link>
        </span>
      </div>

      <p className="text-[11px] text-center text-muted-foreground pt-3">
        By continuing, you agree to our{' '}
        <a
          href="https://app.termly.io/document/terms-of-service/a118a575-bf92-4a88-a954-1589ae572d09"
          target="_blank"
          rel="noopener noreferrer"
          className="underline">
          Terms
        </a>{' '}
        and{' '}
        <a
          href="https://app.termly.io/document/privacy-policy/c16d10b8-1b65-43ea-9568-30e7ce727a60"
          target="_blank"
          rel="noopener noreferrer"
          className="underline">
          Privacy Policy
        </a>
      </p>
    </div>
  );
}

export default RegistrationForm;
